import { BetaSchemaForm } from "@ant-design/pro-components";
import { Button, Modal, Popconfirm, Spin, Steps } from "antd";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { CreationWrapper } from "@/layouts/CreationWrapper";
import { deriveUIDefaultsFromServerFormat } from "./config/ServerToClientConverter";
import { genInstanceProfileForServer } from "./config/ClientToServerConverter";
import { useCreate, useInvalidate } from "@refinedev/core";
import { useParams } from "react-router-dom";
import { useResourceNavigate } from "@/hooks/useResourceNavigate";
import { useResourceContext } from "@/layouts/ResourceContext";
import { useWizardColumnsConfig } from "@/hooks/config/useWizardColumnsConfig";
import { QuestionCircleOutlined } from "@ant-design/icons";

export const Wizard = ({ instanceType, resource, title, description, submitIdx = -1 }) => {
  const formRef = useRef();
  const formMapRef = useRef();
  const [loading, setLoading] = useState(false);
  
  const { dispatch } = useResourceContext();

  const invalidate = useInvalidate();
  const { tenantName: tenant } = useParams();

  const navigate = useResourceNavigate();

  const { t } = useTranslation();
  const [version, setVersion] = useState("");
  const [aZone, setAZone] = useState("");
  const [enableDruid, setEnableDruid] = useState(false);
  const [enableGitea, setEnableGitea] = useState(false);
  const [enableNeo4j, setEnableNeo4j] = useState(false);
  const templateRef = useRef({});
  const initializedRef = useRef({});
  const [currentStep, setCurrentStep] = useState(0);

  const onValuesChange = (changedValues, allValues) => {
    if (changedValues.hasOwnProperty("template")) {
      initializedRef.current = {};
    }
  };

  const onValuesChangeCallback = useCallback((changedValues, allValues) => {
    onValuesChange(changedValues, allValues);
  }, []);

  const { steps, columns, templateFun } = useWizardColumnsConfig({
    tenant,
    formRef,
    onValuesChangeCallback,
    instanceType,
    version,
    aZone,
    enableDruid,
    enableGitea,
    enableNeo4j,
  });

  const [ss, setSS] = useState([...steps]);

  useEffect(() => {
    //if (currentStep == 0) return;
    if (currentStep == 1 && instanceType === "pool-infra") {
      initializedRef.current[currentStep] = true;
      return;
    }
    if (initializedRef.current[currentStep] != true) {
      initializedRef.current[currentStep] = true;
      const templateValue = templateFun[currentStep](templateRef.current);
      formRef.current.setFieldsValue(templateValue);
    }
  }, [currentStep, instanceType]);

  // The "database" nodegroup is always shown on the Nodegroup step (unlike druid-master/
  // druid-worker, which only get added to the schema at all when enableDruid is checked), so it
  // needs its desiredSize driven explicitly off enableNeo4j instead: 1 the moment Neo4j is
  // enabled on the General step, back to 0 if it's unchecked, so it doesn't sit idle for a
  // tenant that never enables Neo4j nor gets left at 0 for one that does. Runs after the
  // template-defaults effect above so it always wins on a first visit to this step too.
  useEffect(() => {
    if (currentStep !== 1) return;
    formRef.current?.setFieldsValue({ "database.desiredSize": enableNeo4j ? 1 : 0 });
  }, [enableNeo4j, currentStep]);

  const onCurrentChange = (current) => {
    if (current === 1 && initializedRef.current[current] != true) {
      const v = formRef.current.getFieldValue("version");
      if (v) {
        setVersion(v.substring(1));
      }
      const zone = formRef.current.getFieldValue("zone");
      setAZone(zone);
      const template = formRef.current.getFieldValue("template-define");
      const dv = deriveUIDefaultsFromServerFormat(template.value.profile);
      templateRef.current = dv;
    }
    if(current === 1 && currentStep === 0) {
      const zone = formRef.current.getFieldValue("zone");
      if(zone != aZone)
        setAZone(zone);

      const druid = formRef.current.getFieldValue("enableDruid");
      if(enableDruid != druid)
        setEnableDruid(druid);

      const gitea = formRef.current.getFieldValue("enableGitea");
      if(enableGitea != gitea)
        setEnableGitea(gitea);

      const neo4j = formRef.current.getFieldValue("enableNeo4j");
      if(enableNeo4j != neo4j)
        setEnableNeo4j(neo4j);
    }
    setCurrentStep(current);
    if(steps.length != ss.length)
      setSS([...steps]);
  };
  const action = resource === "nodegroups" ? "list" : "show";
  const { mutate: create } = useCreate({
    resource,
    successNotification: (data, values, resource) => {
      setLoading(false);
      invalidate({
        resource,
        invalidates: ["list"],
      });

      if (data?.data?.status === "OK") {
        dispatch({
          type: "SELECT_RESOURCE",
          payload: data.data.value,
        });
        navigate({
          resource,
          key: data.data.value.id,
          action,
        });

        return {
          message: t(`${resource}.actionResult.create.success`, {
            id: data?.data?.value?.id,
          }),
          type: "success",
        };
      } else {
        return {
          message: data?.data?.message,
          type: "error",
        };
      }
    },
    errorNotification: (data, values, resource) => {
      setLoading(false);
      return {
        message: t(`${resource}.actionResult.create.failed`, {
          id: data?.data?.value?.id,
        }),
        type: "error",
      };
    },
  });

  const handleSubmit = async (step) => {
    try {
      await formRef.current?.validateFields();
      console.log(formMapRef);
      if(formMapRef && formMapRef.current) {
        if(Object.keys(templateRef.current).length == 0) {
          const template = formRef.current.getFieldValue("template-define");
          const dv = deriveUIDefaultsFromServerFormat(template.value.profile);
          templateRef.current = dv;
        }
        const values =  formMapRef.current.reduce((acc, obj, index) => {
          let value;
          if(index <= step) {
            value = obj.current.getFieldsValue();
          } else {
            value = templateFun[index](templateRef.current);
            // Early submit (submitIdx=0) can finish the wizard without ever visiting the
            // Nodegroup step (always index 1 for a silo instance), which is the only place the
            // enableNeo4j-driven override in the effect above gets applied -- so it needs
            // reapplying here too, off the template fallback, or Neo4j would come up unable to
            // schedule (desiredSize stuck at the template's default of 0). Read off `acc`, not
            // the enableNeo4j state var: that state only syncs from the form when the General
            // step is actually left for the Nodegroup step (see onCurrentChange), which an early
            // submit from step 0 never does, so it would still read stale.
            if (instanceType === "silo" && index === 1) {
              value = { ...value, "database.desiredSize": acc.enableNeo4j ? 1 : 0 };
            }
          }
          return {...acc, ...value};
        }, {})
  
        function filterEmptyValues(obj) {
          const filteredObj = {};
          
          for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
              const value = obj[key];
              if (value !== null && value !== undefined && value !== '') {
                filteredObj[key] = value;
              }
            }
          }
          
          return filteredObj;
        }
        onFinish(filterEmptyValues(values));
      }
    } catch (error) {
      console.error('Form validation failed:', error);
    }
  };

  const onFinish = async (values) => {
    setLoading(true);
    const config = genInstanceProfileForServer({
      ...values,
      type: instanceType,
    });
    const data = tenant !== undefined ? { ...config, tenant } : config;
    // delete data["region"];
    create({
      values: data,
    });
  };

  const submitter = submitIdx != -1 ? {

    render: (props) => {
      if (props.step === 0) {
        return [
          <Button onClick={() => props.onSubmit?.()}>
            Next
          </Button>,
          submitIdx == 0 && <Popconfirm
          title="Launch the instance"
          onConfirm={() => handleSubmit(props.step)}
          description="Are you sure you want to launch the instance with default configuration for left steps?"
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
        ><Button       
            key="submit"
            type="primary"
          >
            Submit
          </Button></Popconfirm>
        ];
      }

      if (props.step > 0 && props.step < steps.length - 1) {
        return [
          <Button key="pre" onClick={() => props.onPre?.()}>
            Previous
          </Button>,
          <Button
            key="next"
            onClick={() => props.onSubmit?.()}
          >
            Next
          </Button>,
          props.step >= submitIdx && <Popconfirm
          title="Launch the instance"
          onConfirm={() => handleSubmit(props.step)}
          description="Are you sure you want to launch the instance with default configuration for left steps?"
          icon={<QuestionCircleOutlined style={{ color: 'red' }} />}
        ><Button       
            key="submit"
            type="primary"
          >
            Submit
          </Button></Popconfirm>
        ];
      }

      return [
        <Button key="gotoTwo" onClick={() => props.onPre?.()}>
          {'<'} Previous
        </Button>,
        <Button
          type="primary"
          key="submit"
          onClick={() => props.onSubmit?.()}
        >
          Submit
        </Button>,
      ];
    },
  } : {};

  return (
    <Spin spinning={loading}>
      <CreationWrapper title={t(title)} desc={t(description)} footer={<></>}>
        <Steps items={steps} current={currentStep}/>
        <BetaSchemaForm
          layoutType="StepsForm"
          onCurrentChange={onCurrentChange}
          formRef={formRef}
          formMapRef={formMapRef}
          onFinish={onFinish}
          steps={ss}
          stepsRender={(steps, dom) => (<div></div>)}
          columns={columns}
          formProps={{
            onValuesChange: onValuesChange,
            // initialValues: formValues,
          }}
          submitter={submitter}
        />
      </CreationWrapper>
    </Spin>
  );
};
